import { useState, useCallback } from "react";

interface HistoryEntry<T> {
  imageData: string;
  extra: T;
}

export function useHistory<T>() {
  const [history, setHistory] = useState<HistoryEntry<T>[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const saveToHistory = useCallback(
    (canvas: HTMLCanvasElement | null, extra: T) => {
      if (!canvas) return;
      const imageData = canvas.toDataURL("image/png", 1.0);
      setHistory((prev) => [
        ...prev.slice(0, historyIndex + 1),
        { imageData, extra },
      ]);
      setHistoryIndex((prev) => prev + 1);
    },
    [historyIndex]
  );

  const undo = useCallback(
    (
      canvas: HTMLCanvasElement | null,
      ctx: CanvasRenderingContext2D | null,
      tempCanvas: HTMLCanvasElement | null,
      onRestore?: (
        extra: T,
        meta?: { width: number; height: number; imageData: string }
      ) => boolean | void
    ) => {
      if (historyIndex <= 0 || !ctx || !canvas) return;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const entry = history[newIndex];
      loadState(entry.imageData, canvas, ctx, tempCanvas, (meta) =>
        onRestore?.(entry.extra, meta)
      );
    },
    [history, historyIndex]
  );

  const redo = useCallback(
    (
      canvas: HTMLCanvasElement | null,
      ctx: CanvasRenderingContext2D | null,
      tempCanvas: HTMLCanvasElement | null,
      onRestore?: (
        extra: T,
        meta?: { width: number; height: number; imageData: string }
      ) => boolean | void
    ) => {
      if (historyIndex >= history.length - 1 || !ctx || !canvas) return;
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const entry = history[newIndex];
      loadState(entry.imageData, canvas, ctx, tempCanvas, (meta) =>
        onRestore?.(entry.extra, meta)
      );
    },
    [history, historyIndex]
  );

  const loadState = (
    dataUrl: string,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    tempCanvas: HTMLCanvasElement | null,
    onLoaded?: (meta: { width: number; height: number; imageData: string }) => boolean | void
  ) => {
    const img = new Image();
    img.onload = () => {
      const meta = { width: img.width, height: img.height, imageData: dataUrl };
      const handled = onLoaded?.(meta);
      if (handled) return;

      canvas.width = img.width;
      canvas.height = img.height;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      if (tempCanvas) {
        const tempContext = tempCanvas.getContext("2d");
        if (tempContext) {
          tempCanvas.width = img.width;
          tempCanvas.height = img.height;
          tempContext.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
        }
      }
    };
    img.src = dataUrl;
  };

  const resetHistory = useCallback(
    (initialState: string, extra: T) => {
      setHistory([{ imageData: initialState, extra }]);
      setHistoryIndex(0);
    },
    []
  );

  const clearToInitial = useCallback(
    (
      canvas: HTMLCanvasElement | null,
      ctx: CanvasRenderingContext2D | null,
      tempCanvas: HTMLCanvasElement | null,
      onRestore?: (
        extra: T,
        meta?: { width: number; height: number; imageData: string }
      ) => boolean | void,
      afterClear?: () => void
    ) => {
      if (!ctx || !canvas || history.length === 0) return;
      const img = new Image();
      img.onload = () => {
        const meta = { width: img.width, height: img.height, imageData: history[0].imageData };
        const handled = onRestore?.(history[0].extra, meta);
        if (!handled) {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          if (tempCanvas) {
            const tempContext = tempCanvas.getContext("2d");
            if (tempContext) {
              tempCanvas.width = img.width;
              tempCanvas.height = img.height;
              tempContext.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
            }
          }
        }
        afterClear?.();
      };
      img.src = history[0].imageData;
    },
    [history]
  );

  return {
    history,
    historyIndex,
    saveToHistory,
    undo,
    redo,
    resetHistory,
    clearToInitial,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
  };
}
